import { modalController } from '@ionic/core';
import { Component, Event, EventEmitter, forceUpdate, Fragment, h, Host, Prop, State } from '@stencil/core';
import { PersonDoc, WorkflowState } from 'common';
import { PeopleService } from 'src/global/service/PeopleService';
import { ConcoursePersonSelector } from '../../components/concourse-person-selector/concourse-person-selector';

@Component({
  tag: 'workflow-state-modal',
})
export class WorkflowStateModal {
  static async present(projectId: string, state: WorkflowState, feature: string, featureId: string, canDelete: boolean): Promise<WorkflowState | string | null> {
    const modal = await modalController.create({
      component: 'workflow-state-modal',
      cssClass: 'modal-height-auto',
      componentProps: { state, projectId, feature, featureId, canDelete },
      backdropDismiss: false,
    });
    return new Promise(async resolve => {
      modal.addEventListener('save', (e: CustomEvent) => {
        modal.dismiss();
        resolve(e.detail);
      });
      modal.addEventListener('delete', (e: CustomEvent) => {
        modal.dismiss();
        resolve('delete');
      });
      modal.addEventListener('cancel', () => {
        modal.dismiss();
        resolve(null);
      });
      await modal.present();
    });
  }
  @Prop() state: WorkflowState;
  @Prop() feature: string;
  @Prop() featureId: string;
  @Prop() canDelete: boolean = false;
  @Prop() isInitial: boolean = false;

  @Event() save: EventEmitter<WorkflowState>;
  @Event() delete: EventEmitter<string>;
  @Event() cancel: EventEmitter<void>;
  @State() edit: WorkflowState;
  @State() errorMessage: string;

  @Prop() projectId: string;
  private people: PersonDoc[];

  get featureName(): string {
    return this.feature.charAt(0).toUpperCase() + this.feature.slice(1);
  }

  get featureSingular(): string {
    return this.featureName.replace(/s$/, '');
  }

  async componentWillLoad() {
    // Deep copy to avoid mutating the original until save
    this.edit = { ...this.state, dueBy: this.state.dueBy || [] };
    this.people = await PeopleService.getPersonDocs(this.projectId);
  }

  /*
  get dueByPeople(): PersonDoc[] {
    return this.edit.dueBy ? this.people.filter(p => this.edit.dueBy.includes(p._id)) : [];
  }*/

  async addPerson() {
    const people = await ConcoursePersonSelector.present({
      projectId: this.projectId,
      feature: this.feature,
      featureId: this.featureId,
      peopleToOmitIds: this.edit.dueBy,
      needsWrite: true,
    });
    if (people && people.length > 0) {
      this.edit.dueBy = [...(this.edit.dueBy || []), ...people.map(p => p._id)];
      for (const person of people) {
        if (!this.people.find(p => p._id == person._id)) {
          this.people.push(person);
        }
      }
      forceUpdate(this);
    }
  }

  removePerson(id: string) {
    this.edit.dueBy = this.edit.dueBy.filter(p => p !== id);
    forceUpdate(this);
  }

  saveState() {
    if (!this.edit.action) {
      this.errorMessage = 'Action is required';
      return;
    }
    if (this.edit.action.length > 20) {
      this.errorMessage = 'Action is too long, must be less than 20 characters';
      return;
    }
    if (!this.isInitial && this.edit.dueDays < 2) {
      this.errorMessage = 'You must give the responsible parties at least 2 days to complete the action';
      return;
    }
    this.errorMessage = '';
    this.save.emit(this.edit);
  }

  render() {
    return (
      <Host class="concourse-modal">
        <modal-header
          modalTitle={this.state.action ? `Edit ${this.featureSingular} ${this.state.action} step` : `Add ${this.featureSingular} step`}
          onDismiss={() => this.cancel.emit()}
        />
        <div class="modal-content">
          <ion-grid>
            <ion-row>
              <ion-col size="12">
                <ion-label class="typography-body2-400" style={{ marginBottom: '6px', color: 'var(--background-color-step-950)' }}>
                  Due By
                </ion-label>
              </ion-col>
            </ion-row>
            <ion-row>
              <ion-col size="12" class="ion-display-flex ion-flex-wrap ion-align-items-center">
                {this.edit.dueBy.map(id => {
                  const personDoc = this.people.find(p => p._id === id && p.deleted_date === undefined);
                  if (personDoc) {
                    return <person-chip person={personDoc} removable={true} onRemove={e => this.removePerson(personDoc._id)}></person-chip>;
                  }
                  return (
                    <ion-chip>
                      {id}
                      <ion-icon color="light" name="close-circle" onClick={e => this.removePerson(id)}></ion-icon>
                    </ion-chip>
                  );
                })}
                <ion-button fill="clear" size="small" shape="round" onClick={() => this.addPerson()}>
                  <ion-icon slot="start" name="add-outline" color="primary"></ion-icon>
                  add
                </ion-button>
              </ion-col>
            </ion-row>
            <ion-row>
              <ion-col size="12">
                {!this.isInitial && (
                  <ion-input
                    label="Due Days"
                    label-placement="stacked"
                    type="number"
                    value={this.edit.dueDays}
                    onIonInput={e => (this.edit.dueDays = parseInt((e.target as HTMLInputElement).value, 10))}
                  />
                )}
              </ion-col>
              <ion-col size="12">
                <ion-row>
                  {!this.isInitial && (
                    <ion-input
                      label="Action"
                      label-placement="stacked"
                      type="string"
                      value={this.edit.action}
                      onIonInput={e => (this.edit.action = (e.target as HTMLInputElement).value)}
                    />
                  )}
                </ion-row>
              </ion-col>
              <ion-col size="12">
                <ion-row>
                  <ion-input
                    label="Past Tense"
                    label-placement="stacked"
                    type="string"
                    value={this.edit.pastTense}
                    onIonInput={e => (this.edit.pastTense = (e.target as HTMLInputElement).value)}
                  />
                </ion-row>
              </ion-col>
            </ion-row>
          </ion-grid>
        </div>
        <ion-footer>
          {this.errorMessage && (
            <ion-text color="danger" class="cc-margin-horizontal">
              {this.errorMessage}
            </ion-text>
          )}
          <ion-toolbar>
            {this.canDelete && (
              <ion-button fill="clear" slot="start" color="danger" onClick={() => this.delete.emit(this.state.id)}>
                <ion-icon slot="start" name="trash-outline"></ion-icon>
                Delete
              </ion-button>
            )}
            <ion-button color="primary" fill="solid" slot="end" shape="round" onClick={() => this.saveState()}>
              {this.state.action ? 'Save Step' : 'Add'}
            </ion-button>
          </ion-toolbar>
        </ion-footer>
      </Host>
    );
  }
}
